import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { getApiErrorMessage, portalApi } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { TotpInput } from '../../components/ui/TotpInput';
import { useAuth } from '../../context/AuthContext';
import type { SettlementPreview, WalletChangelogEntry } from '../../api/types';

export function SettlementWalletPage() {
  const { refresh, user } = useAuth();
  const [settlement, setSettlement] = useState<SettlementPreview | null>(null);
  const [changelog, setChangelog] = useState<WalletChangelogEntry[]>([]);
  const [wallet, setWallet] = useState('');
  const [totp, setTotp] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const totpEnabled = user?.totp_enabled === true;

  const load = () => {
    setLoading(true);
    void Promise.all([portalApi.getSettlementWallet(), portalApi.getSettlementWalletChangelog(100)])
      .then(([w, c]) => {
        setSettlement(w.settlement);
        setWallet(w.settlement.configured ? w.settlement.solana_wallet : '');
        setChangelog(c.changelog ?? []);
      })
      .catch((ex) => setErr(getApiErrorMessage(ex)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    if (!totpEnabled) {
      setErr('Enable two-factor authentication in Security before changing settlement wallet');
      return;
    }
    if (totp.length !== 6) {
      setErr('Enter your 6-digit 2FA code');
      return;
    }
    setSaving(true);
    try {
      const r = await portalApi.updateSettlementWallet(wallet.trim(), totp);
      setMsg(r.message ?? 'Settlement wallet saved');
      setTotp('');
      setSettlement((prev) =>
        prev
          ? {
              ...prev,
              solana_wallet: r.solana_wallet,
              merchant_vault: r.merchant_vault,
              configured: true,
            }
          : prev,
      );
      await refresh();
      load();
    } catch (ex) {
      setErr(getApiErrorMessage(ex));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="font-display text-2xl flex items-center gap-2">
          <Wallet size={26} />
          Settlement wallet
        </h2>
        <p className="text-sm text-onyx-muted mt-2 leading-relaxed">
          On-chain payouts use the <strong>Onyx Pay splitter</strong> program: 98.5% to your wallet, 1.5%
          platform fee (1.4% treasury + 0.1% gas) — atomically on Solana. Required before POS terminals can
          accept crypto.
        </p>
      </div>

      {err ? <p className="text-sm text-onyx-danger">{err}</p> : null}
      {msg ? <p className="text-sm text-onyx-success">{msg}</p> : null}

      {!totpEnabled && !loading ? (
        <div className="rounded-xl border border-onyx-warn/40 bg-onyx-warn/10 px-4 py-3 text-sm">
          <strong className="text-onyx-warn">2FA required.</strong> Settlement wallet cannot be changed until you
          enable authenticator app 2FA in{' '}
          <Link to="/security" className="underline font-semibold">
            Security settings
          </Link>
          .
        </div>
      ) : null}

      <Card title="Solana owner wallet">
        {loading ? (
          <p className="text-sm text-onyx-muted">Loading…</p>
        ) : (
          <form onSubmit={(e) => void onSave(e)} className="space-y-4">
            <div>
              <label className="text-xs text-onyx-muted block mb-1">Wallet address (base58)</label>
              <Input
                placeholder="Your Solana wallet that receives USDT"
                value={wallet}
                onChange={(e) => setWallet(e.target.value)}
                className="font-mono text-sm"
              />
            </div>

            {totpEnabled ? (
              <div>
                <label className="text-xs text-onyx-muted block mb-1">2FA code (required to save)</label>
                <TotpInput value={totp} onChange={setTotp} />
              </div>
            ) : null}

            {settlement?.configured && settlement.merchant_vault ? (
              <div className="rounded-xl border border-onyx-border bg-onyx-elevated/50 p-4 text-sm space-y-2">
                <p>
                  <span className="text-onyx-muted">USDT token account (ATA):</span>
                  <br />
                  <span className="font-mono text-xs break-all">{settlement.merchant_vault}</span>
                </p>
                {settlement.updated_at ? (
                  <p className="text-xs text-onyx-muted">
                    Last updated {new Date(settlement.updated_at).toLocaleString()}
                    {settlement.updated_by ? ` · ${settlement.updated_by}` : ''}
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-onyx-warn">
                Not configured yet — enter the wallet where you want to receive USDT from customer payments.
              </p>
            )}

            <Button type="submit" disabled={saving || !totpEnabled || wallet.trim().length < 32}>
              {saving ? 'Saving…' : settlement?.configured ? 'Update wallet' : 'Save wallet'}
            </Button>
          </form>
        )}
      </Card>

      <Card title="Change history">
        {changelog.length === 0 ? (
          <p className="text-sm text-onyx-muted">No changes yet.</p>
        ) : (
          <ul className="space-y-3 text-sm">
            {changelog.map((e) => (
              <li key={e.id} className="border-b border-onyx-border pb-2 last:border-0">
                <p className="text-onyx-muted text-xs">{new Date(e.created_at).toLocaleString()}</p>
                <p>
                  <span className="font-mono text-xs break-all">{e.previous_wallet.slice(0, 8)}…</span>
                  {' → '}
                  <span className="font-mono text-xs break-all">{e.new_wallet.slice(0, 8)}…</span>
                </p>
                <p className="text-xs text-onyx-muted">
                  {e.changed_by_type} · {e.changed_by_email ?? '—'}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Compliance note">
        <p className="text-xs text-onyx-muted leading-relaxed">
          Changes are stored in the database with a full changelog (who / when / previous address). Each
          verified payment links to an order ID, transaction signature, and on-chain split amounts for
          audits and disputes.
        </p>
      </Card>
    </div>
  );
}
